import React, { useEffect, useState } from 'react';
import {
  Users,
  User,
  GraduationCap,
  Phone,
  Calendar,
  Trash2,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Hash,
  Award,
  Eye,
  X,
  MapPin,
  BookOpen,
  FileText,
  Download,
  CheckCircle,
  AlertCircle,
  Home,
  Building2,
  XIcon
} from 'lucide-react';
import './ReviewerSubmissions.css';
import { ApiUrl, BaseUrl } from '../API/ApiUrl';

const ReviewerSubmissions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const itemsPerPage = 10;

  const [reviewers, setReviewers] = useState([]);

  const fetchReviewSubmissions = async () => {
    const token = localStorage.getItem("adminToken");

    const response = await fetch(`${ApiUrl}/review-submission`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();
    setReviewers(data);
  };

  useEffect(() => {
    fetchReviewSubmissions();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this reviewer?')) {
      try {
        const token = localStorage.getItem("adminToken");
        await fetch(`${ApiUrl}/review-submission/${id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setReviewers(reviewers.filter(item => item.id !== id));
      } catch (error) {
        console.error('Error deleting reviewer:', error);
      }
    }
  };

  const handleView = async (reviewer) => {
    const token = localStorage.getItem("adminToken");

    try {
      await fetch(`${ApiUrl}/review-submission/read/${reviewer.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSelectedReviewer(reviewer);
      setShowModal(true);
      fetchReviewSubmissions();

    }
    catch (error) {
      console.error("View Error:", error);
    }
  }
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReviewer(null);
  };

  const handleDownloadResume = (resumeFile) => {
    if (resumeFile) {
      window.open(`${BaseUrl}/uploads/${resumeFile}`, '_blank');
    }
  };

  // Filter reviewers based on search
  const filteredReviewers = reviewers.filter(item =>
    item.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.qualification?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.contact_number?.includes(searchTerm) ||
    item.research_domain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredReviewers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedReviewers = filteredReviewers.slice(startIndex, startIndex + itemsPerPage);

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: "N/A", time: "" };

    const dateObj = new Date(dateString);

    const date = dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const time = dateObj.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return { date, time };
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { label: 'Pending', class: 'pending' },
      'approved': { label: 'Approved', class: 'approved' },
      'rejected': { label: 'Rejected', class: 'rejected' },
      'under-review': { label: 'Under Review', class: 'under-review' }
    };
    const config = statusConfig[status] || { label: status || 'Pending', class: 'pending' };
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm ]);
  
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages]);

  return (
    <div className="reviewer-submissions-page">
      {/* Header */}
      <div className="page-header">
        <div className="title-section">
          <Users size={24} className="title-icon" />
          <div>
            <h1 className="page-title">Reviewer Submissions</h1>
            <p className="page-subtitle">Manage reviewer applications and information</p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, qualification, phone, or domain..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <XIcon
              size={16}
              className="clear-icon"
              onClick={() => setSearchTerm("")}
            />
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {/* <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon">
            <Users size={16} />
          </div>
          <div className="stat-info">
            <span className="stat-label">Total Reviewers</span>
            <span className="stat-value">{reviewers.length}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Award size={16} />
          </div>
          <div className="stat-info">
            <span className="stat-label">Ph.D. Holders</span>
            <span className="stat-value">{reviewers.filter(r => r.qualification?.includes('Ph.D.')).length}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Calendar size={16} />
          </div>
          <div className="stat-info">
            <span className="stat-label">This Month</span>
            <span className="stat-value">
              {reviewers.filter(r => {
                const { date } = formatDateTime(r.created_at);
                const currentMonth = new Date().getMonth();
                const reviewerMonth = new Date(r.created_at).getMonth();
                return reviewerMonth === currentMonth;
              }).length}
            </span>
          </div>
        </div>
      </div> */}

      {/* Table */}
      <div className="table-container">
        <div className="table-header">
          <h3>Reviewer List</h3>
          <span className="record-count">{filteredReviewers.length} reviewers</span>
        </div>

        <div className="table-responsive">
          <table className="reviewer-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Qualification</th>
                <th>Phone</th>
                <th>Date</th>
                <th>Actions</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {paginatedReviewers.map((item, index) => (
                <tr key={item.id}  className={item.is_read === 0 ? 'unread-row' : ''}>
                  <td>
                    <span className="sl-no">{startIndex + index + 1}</span>
                  </td>
                  <td>
                    <div className="name-wrapper">
                      <User size={13} className="name-icon" />
                      <span className="reviewer-name">{item.full_name}</span>
                      {item.is_read === 0 && (
                        <span className="unread-badge">New</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="name-wrapper">
                      <GraduationCap size={13} className="name-icon" />
                      <span className="qualification-text">{item.qualification}</span>
                    </div>
                  </td>
                  <td>
                    <div className="name-wrapper">
                      <Phone size={13} className="name-icon" />
                      <span className="phone-text">{item.contact_number}</span>
                    </div>
                  </td>
                  <td>
                    {(() => {
                      const { date } = formatDateTime(item.created_at);
                      return (
                        <div className="date-wrapper">
                          <Calendar size={12} className="date-icon" />
                          <span className="date-text">{date}</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    <button
                      className="action-btn view"
                      onClick={() => handleView(item)}
                      title="View Details"
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(item.id)}
                      title="Delete Reviewer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredReviewers.length === 0 && (
            <div className="empty-state">
              <Users size={40} />
              <p>No reviewers found</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredReviewers.length > 0 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
            </button>

            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* View Modal */}
      {showModal && selectedReviewer && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="reviewer-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reviewer Details</h3>
              <button className="close-btn" onClick={handleCloseModal}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              {/* ID */}
              {/* <div className="detail-section">
                <h4>
                  <Hash size={16} />
                  Application ID
                </h4>
                <p className="id-value">#{selectedReviewer.id}</p>
              </div> */}

              {/* Full Name */}
              <div className="detail-row">

                <div className="detail-half">
                  <h4>
                    <User size={16} />
                    Full Name
                  </h4>
                  <p className="name-value">{selectedReviewer.full_name}</p>
                </div>

                {/* Qualification */}
                <div className="detail-half">
                  <h4>
                    <GraduationCap size={16} />
                    Qualification
                  </h4>
                  <p>{selectedReviewer.qualification}</p>
                </div>
              </div>

              {/* Contact Numbers */}
              <div className="detail-row">
                <div className="detail-half">
                  <h4>
                    <Phone size={14} />
                    Residential Contact
                  </h4>
                  <p className="contact-value">{selectedReviewer.residential_contact || 'N/A'}</p>
                </div>
                <div className="detail-half">
                  <h4>
                    <Phone size={14} />
                    Mobile Number
                  </h4>
                  <p className="contact-value">{selectedReviewer.contact_number}</p>
                </div>
              </div>

              {/* Institution Details */}
              <div className="detail-section">
                <h4>
                  <Building2 size={16} />
                  Institution Name
                </h4>
                <p>{selectedReviewer.institution_name}</p>
              </div>

              <div className="detail-section">
                <h4>
                  <MapPin size={16} />
                  Institution Address
                </h4>
                <p className="address-text">{selectedReviewer.institution_address}</p>
              </div>

              {/* Research Domain */}
              <div className="detail-section">
                <h4>
                  <BookOpen size={16} />
                  Research Domain
                </h4>
                <div className="domain-tags">
                  {selectedReviewer.research_domain?.split(',').map((domain, idx) => (
                    <span key={idx} className="domain-tag">{domain.trim()}</span>
                  ))}
                </div>
              </div>

              {/* Resume File */}
              <div className="detail-section">
                <h4>
                  <FileText size={16} />
                  Resume
                </h4>
                {selectedReviewer.resume_file ? (
                  <div className="file-item">
                    <FileText size={16} className="file-icon pdf" />
                    <span className="file-name">{selectedReviewer.resume_file}</span>
                    <button
                      className="file-download-btn"
                      onClick={() => handleDownloadResume(selectedReviewer.resume_file)}
                      title="Download Resume"
                    >
                      <Download size={14} />
                      <span>Download</span>
                    </button>
                  </div>
                ) : (
                  <p className="no-file">No resume uploaded</p>
                )}
              </div>

              {/* Status */}
              {/* <div className="detail-section">
                <h4>
                  <AlertCircle size={16} />
                  Status
                </h4>
                <div className="status-wrapper">
                  {getStatusBadge(selectedReviewer.status)}
                </div>
              </div> */}

              {/* Created At */}
              <div className="detail-section">
                <h4>
                  <Calendar size={16} />
                  Submitted On
                </h4>
                <p>
                  {(() => {
                    const { date, time } = formatDateTime(selectedReviewer.created_at);
                    return `${date} at ${time}`;
                  })()}
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal}>
                Close
              </button>
              {/* <button 
                className="btn-primary"
                onClick={() => handleDownloadResume(selectedReviewer.resume_file)}
                disabled={!selectedReviewer.resume_file}
              >
                <Download size={14} />
                <span>Download Resume</span>
              </button> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewerSubmissions;